import { SystemUserTypes } from '../types';
import { store } from '../store';
import { api } from '../http';
import { getEffectiveSetting, SETTING } from '../hooks/use-effective-settings';

/**
 * Zendesk messaging integration.
 *
 * The signing of the user JWT lives in pom-2-api (GET /v1/support/chat-token),
 * NOT here. It used to be signed in the browser, which required shipping the
 * Zendesk signing secret in the bundle as
 * REACT_APP_ZENDESK_USER_JWT_USER_SECRET_KEY - readable by anyone, who could
 * then mint a token for any email and impersonate that customer in Zendesk.
 *
 * Two consequences of moving it server-side:
 *  - there is no build-time secret to gate on, so the widget always loads. The
 *    previous secrets guard is why the chat bubble disappeared from prod
 *    entirely once it shipped (the CI/Amplify build has no such vars).
 *  - authentication is best-effort. If the token call fails, the bubble stays
 *    and chat still works, just unauthenticated - far better than no bubble.
 */

// The widget key is public by design - it travels in the snippet URL that every
// visitor's browser fetches. It is not a credential.
const WIDGET_KEY = '399ee080-cb95-48fd-9563-4182606e3a3e';
const WIDGET_SRC = `https://static.zdassets.com/ekr/snippet.js?key=${WIDGET_KEY}`;
const SNIPPET_ELEMENT_ID = 'ze-snippet';

// Poll for the widget to finish booting. Bounded so a blocked/failed snippet
// load (ad blocker, CSP, offline) can't leave a 1s timer running forever.
const READY_POLL_MS = 1000;
const READY_MAX_ATTEMPTS = 30;

interface ChatTokenResult {
    token: string;
    expiresIn: number;
    conversationFields: { id: string; value: string }[];
}

const Zendesk = {
    initialize: (): (() => void) => {
        // Load the widget unconditionally - the bubble must not depend on
        // whether chat auth happens to be configured in this environment, and it
        // must be present on the LOGIN page, before anyone is authenticated.
        if (!document.getElementById(SNIPPET_ELEMENT_ID)) {
            const script = document.createElement('script');
            script.id = SNIPPET_ELEMENT_ID;
            script.src = WIDGET_SRC;
            script.async = true;
            script.onerror = () => {
                console.error('[Zendesk] Failed to load the messaging widget script.');
            };
            document.body.appendChild(script);
        }

        // initialize() runs once, on mount, which on a cold load is the login
        // page - so there is no user yet. Watch the auth store instead of reading
        // it once, otherwise a visitor who chats anonymously and then logs in
        // would stay anonymous for the whole session.
        let widgetReady = typeof window.zE !== 'undefined';
        let syncedUserId: string | null = null;
        let syncing = false;
        // Tracks whether WE currently believe the launcher is shown, for
        // staff OR the org-level chat-mode reason below, so a re-check on
        // every store change only calls zE('messenger', ...) on the actual
        // true<->false transition, not on every unrelated store update.
        let zendeskShown = true;

        const syncIdentity = async () => {
            if (!widgetReady || syncing) return;

            const user = store.getState().auth.authUser;
            // A POM Master's OWN session never sees Zendesk (unchanged). A
            // masquerading session used to be forced into this branch too
            // (Carlos, 9/9), but Carlos reversed that (9/14): masquerade must
            // now see exactly what the masqueraded customer would see, so it
            // falls through to the normal per-org chatSupport.mode sync below
            // instead of being force-hidden here. `user?.pomMaster` is never
            // true for a masqueraded identity (it carries the real
            // customer's own fields), so no extra masquerade check is needed
            // - removing the old `user?.masquarade ||` clause is sufficient.
            const isStaff = Boolean(user?.pomMaster);

            // Hide the launcher entirely and skip identity sync altogether
            // for a POM Master's own session, so authenticateUser's
            // chat-token fetch + loginUser call never run for staff. Also
            // covers the widget becoming ready WHILE already staff (this
            // runs on that first ready-triggered call too, not just on a
            // subsequent store change).
            if (isStaff) {
                if (zendeskShown) {
                    zendeskShown = false;
                    if (typeof window.zE !== 'undefined') window.zE('messenger', 'hide');
                }
                return;
            }

            // Real customer: which surface(s) their org is configured to see
            // (Carlos, 9/11 - `chatSupport.mode`, resolved server-side,
            // cascading system -> customer -> group -> team). Default
            // 'zendesk_chat_only' is today's behavior for every org that
            // hasn't been explicitly flipped over.
            const chatMode = await getEffectiveSetting<string>(SETTING.chatSupportMode, 'zendesk_chat_only');
            const shouldShowZendesk = chatMode !== 'ai_chat_only';

            if (shouldShowZendesk !== zendeskShown) {
                zendeskShown = shouldShowZendesk;
                if (typeof window.zE !== 'undefined') window.zE('messenger', shouldShowZendesk ? 'show' : 'hide');
            }

            if (!shouldShowZendesk) {
                // ai_chat_only: never authenticate this customer into
                // Zendesk at all - `syncedUserId` stays untouched, so if the
                // org is later flipped back to showing Zendesk, the normal
                // sync below still runs for this same user instead of
                // short-circuiting on a stale "already synced" check.
                return;
            }

            const userId = user?.id ? String(user.id) : null;

            if (userId === syncedUserId) return;

            syncing = true;
            try {
                if (userId) {
                    syncedUserId = userId;
                    await Zendesk.authenticateUser(user);
                } else {
                    // Logged out: hand the conversation back to an anonymous
                    // visitor so the next person on this browser is not still
                    // talking as the previous user.
                    syncedUserId = null;
                    if (Zendesk.isAuthenticated) Zendesk.onLogout();
                }
            } finally {
                syncing = false;
            }
        };

        const unsubscribe = store.subscribe(() => {
            void syncIdentity();
        });

        let attempts = 0;
        const interval = setInterval(() => {
            attempts += 1;

            if (typeof window.zE !== 'undefined') {
                clearInterval(interval);
                widgetReady = true;
                // Covers the already-logged-in case (e.g. a page refresh with a
                // live session), where no store change will follow.
                void syncIdentity();
                return;
            }

            if (attempts >= READY_MAX_ATTEMPTS) {
                clearInterval(interval);
                console.warn(
                    `[Zendesk] Messaging widget did not become ready after ${READY_MAX_ATTEMPTS} attempts.`,
                );
            }
        }, READY_POLL_MS);

        return () => {
            clearInterval(interval);
            unsubscribe();
        };
    },

    authenticateUser: async (user: SystemUserTypes.Model): Promise<void> => {
        if (typeof window.zE === 'undefined') return;

        // Anonymous visitor (e.g. the login screen). Widget stays, no login.
        if (!user || !user.id) return;

        let chat: ChatTokenResult;
        try {
            const { data } = await api.get('/support/chat-token');
            chat = data?.result;
            if (!chat?.token) throw new Error('Response contained no token');
        } catch (err: any) {
            // 503 = this environment has no Zendesk secret configured. Anything
            // else is a real failure. Either way the bubble remains usable.
            const status = err?.response?.status;
            console.warn(
                status === 503
                    ? '[Zendesk] Chat auth not configured for this environment; continuing unauthenticated.'
                    : `[Zendesk] Could not obtain a chat token (${status ?? err?.message}); continuing unauthenticated.`,
            );
            return;
        }

        window.zE(
            'messenger',
            'loginUser',
            (callback: Function) => {
                callback(chat.token);
            },
            (err: any) => {
                if (err) {
                    console.error('Zendesk authentication failed: ', err);
                    return;
                }
                Zendesk.isAuthenticated = true;
                // Conversation fields come from the server alongside the token so
                // the ids and the customer name stay in one place.
                Zendesk.setConversationFields(chat.conversationFields);
            },
        );
    },

    onLogout: () => {
        if (typeof window.zE === 'undefined') return;
        window.zE('messenger', 'logoutUser');
        Zendesk.isAuthenticated = false;
    },

    setConversationFields: (fields: { id: string; value: any }[]) => {
        if (typeof window.zE === 'undefined' || !fields?.length) return;
        window.zE('messenger:set', 'conversationFields', fields);
    },

    isAuthenticated: false,
};

export default Zendesk;
